import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {currentResource, labels} from '../../../../util/utils';
import SelectDropdown from '../../SelectDropdown/SelectDropdown';
import {isEmpty, orderBy} from 'lodash';
import {AssignmentStatuses, engagementUserStatus, serviceURL, clientSideEventNames} from '../../../../util/uiconstants';
import Evidence from './Evidence';
import {useSelector} from 'react-redux';
import {useDispatchAction} from '../../CustomHooks/UseDispatchAction';
import {getHandOffEvidenceDocuments} from '../../../../actions/Document/documentactions';
import Button from '../../BaseComponentWrappers/MotifButton';
import {getTaskById} from '../../../../actions/Ares/TaskList/tasklistactions';
import {getTaskAssignments, handOffTask, getDocumentSignOffs} from '../../../../actions/Task/signoffsaction';
import {getInternalUsers} from '../../../../actions/InternalUsers/internalusersactions';
import Utility from '../../../../util/utilityfunctions';
import EllipsesControl from '../../EllipsesControl/EllipsesControl';
import env from '../../../../util/env';

function HandOff({setEnableConfirm, engagementId, taskId, assignments, documentId}, ref) {
	const dispatch = useDispatchAction();

	useImperativeHandle(ref, () => {
		return {
			onConfirm
		};
	});

	const documents = useSelector((store) => {
		if (Array.isArray(store.documents.allIds)) {
			return store.documents.allIds.map((id) => store.documents.byId[id]);
		}

		return [];
	});

	const task = useSelector((store) => store.aresTaskList.byId[taskId]);

	const internalUsers = useSelector((store) => {
		if (Array.isArray(store.internalUsers.allIds)) {
			return store.internalUsers.allIds.map((id) => store.internalUsers.byId[id]);
		}

		return null;
	});

	const taskDetails = useSelector((store) => {
		if (store.aresTaskDetails && store.aresTaskDetails.hasOwnProperty(taskId)) {
			return store.aresTaskDetails[taskId];
		}

		return null;
	});

	const isLoading = useSelector((store) => store.isLoading);

	const formattedAssignment = useMemo(() => {
		if (Array.isArray(assignments) && assignments.length > 0) {
			const mapped = assignments
				.filter((assignment) => !Utility.isEmptyGuid(assignment.assignedToUserId))
				.map((assignment) => {
					let label = `${assignment.firstName} ${assignment.lastName}`;

					return {
						...assignment,
						value: assignment.assignedToUserId,
						label: label.trim() !== '' ? label : assignment.assignedToUserId.toString()
					};
				});

			function comparer(a, b) {
				if (a.label.toLowerCase() < b.label.toLowerCase()) {
					return -1;
				}

				if (a.label.toLowerCase() > b.label.toLowerCase()) {
					return 1;
				}

				return 0;
			}

			mapped.sort(comparer);

			return mapped;
		}

		return [];
	}, [assignments]);

	const defaultValue = useMemo(() => {
		let selected = null;

		if (Array.isArray(assignments) && assignments.length > 0) {
			const ordered = orderBy(assignments, ['dueDays'], ['desc']);

			const notCompletedAssignments = ordered.filter(
				(o) => o.assignmentStatusId !== AssignmentStatuses.Complete && !Utility.isEmptyGuid(o.assignedToUserId)
			);

			if (notCompletedAssignments.length > 0) {
				const assignment = notCompletedAssignments[0];

				let label = `${assignment.firstName} ${assignment.lastName}`;

				return {
					...assignment,
					value: assignment.assignedToUserId,
					label: label.trim() !== '' ? label : assignment.assignedToUserId
				};
			}
		}

		return selected;
	}, [assignments]);

	const evidences = useMemo(() => {
		if (Array.isArray(documents) && task) {
			return documents.filter((doc) => task.evidenceDocumentIds?.includes(doc.id));
		}

		return [];
	}, [documents, task]);

	const [handOffTo, setHandOffTo] = useState(defaultValue);
	const [allSignOff, setAllSignOff] = useState(null);
	const [documentsSignOff, setDocumentsSignOff] = useState(new Map());

	const initialRender = useRef(true);

	useEffect(() => {
		dispatch(getHandOffEvidenceDocuments(engagementId, taskId));

		if (taskDetails === null) {
			dispatch(getTaskById(engagementId, taskId));
		}

		if (isEmpty(internalUsers)) {
			dispatch(getInternalUsers(engagementId, [engagementUserStatus.Active], null, -1, 1));
		}
	}, []);

	useEffect(() => {
		if (evidences.length > 0 && initialRender.current) {
			const map = new Map();

			evidences.forEach((doc) => {
				map.set(doc.id, 3);
			});

			setDocumentsSignOff(map);
			setAllSignOff(3);

			initialRender.current = false;
		}
	}, [evidences]);

	useEffect(() => {
		setEnableConfirm(handOffTo ? true : false);
	}, [handOffTo]);

	function onConfirm() {
		if (taskDetails === null || internalUsers === null) {
			return;
		}

		const assignedUser = internalUsers.find((user) => user.userId === handOffTo.value);

		const signOffdocuments = [];

		documentsSignOff.forEach((value, documentId) => {
			if (value === 1 || value === 2) {
				const document = documents.find((doc) => doc.id === documentId);
				if (document) {
					signOffdocuments.push({
						id: documentId,
						documentTypeId: document.documentTypeId,
						isPreparer: value === 1
					});
				}
			}
		});

		const currentAssignment = {
			currentAssignedRoleId: handOffTo.assignedToRoleId,
			currentAssignedToUserId: handOffTo.value,
			currentAssignedToEmailAddress: handOffTo.emailAddress,
			currentAssignedToUserInitials: handOffTo.userInitials
		};

		const payload = {
			id: taskId,
			engagementId,
			isEPSignOffUserRequired: taskDetails.isEPSignoffUserRequired,
			isEQRSignOffUserRequired: taskDetails.isEQRSignoffUserRequired,
			isAssignmentMarkedForCompletion: true,
			isMilestoneTask: false,
			isReturnEvidenceToTask: false,
			operationType: 'handoff',
			handOffUserId: handOffTo.value,
			taskAssigedUserId: taskDetails.taskAssignedToUserId,
			handOffUserEngagementRoleId: assignedUser ? assignedUser.engagementRoleId : 0,
			documents: signOffdocuments
		};

		return dispatch(handOffTask(engagementId, taskId, payload, currentAssignment)).then(() => {
			dispatch(getTaskAssignments(engagementId, taskId));
			getApprovals();
		});
	}

	function onHandOffChange(value) {
		setHandOffTo(value);
	}

	function onDocumentSignOffChange(documentId, value) {
		const map = new Map(documentsSignOff);

		map.set(documentId, value);

		setDocumentsSignOff(map);

		const values = Array.from(map.values());

		let signOff = null;

		if (values.every((value) => value === 1)) {
			signOff = 1;
		}

		if (values.every((value) => value === 2)) {
			signOff = 2;
		}

		if (values.every((value) => value === 3)) {
			signOff = 3;
		}

		setAllSignOff(signOff);
	}

	function onAllSignOffChange(value) {
		const map = new Map();

		evidences.forEach((doc) => {
			map.set(doc.id, value);
		});

		setDocumentsSignOff(map);
		setAllSignOff(value);
	}

	const ellipseOption = ({value, label}) => {
		return <EllipsesControl content={label} tooltip={label} noOfLines={1} id={value} />;
	};

	const getApprovals = () => {
		let requestURL = env.getBaseURL(serviceURL, engagementId) + 'Documents/' + documentId + '/Approvals';

		dispatch(getDocumentSignOffs(requestURL)).then(() => {
			window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
		});
	};

	return (
		<StyledHandOff className={`StyledHandOff`}>
			<section className="description">{currentResource.handOffModal.description}</section>
			<section className="hand-off-dropdown">
				<SelectDropdown
					className="dropdown"
					formatOptionLabel={ellipseOption}
					formLabel={currentResource.handOffModal.dropdownLabel}
					isSearchable
					id="hand-off-dropdown"
					options={formattedAssignment}
					noOptionsMessage={() => labels.noResultsFound}
					isClearable={false}
					isDisabled={false}
					defaultValue={handOffTo}
					value={handOffTo}
					onChange={onHandOffChange}
				/>
			</section>
			{!isLoading && (
				<section className="evidence">
					<section className="evidence-title">
						<section className="evidenceTitleWrapper">
							<section className="title">{currentResource.handOffModal.evidence}</section>
							<section className="existing-signoffs">
								<EllipsesControl
									content={currentResource.handOffModal.existingSignOffs}
									tooltip={currentResource.handOffModal.existingSignOffs}
									noOfLines={1}
									id={currentResource.handOffModal.existingSignOffs}
								/>
							</section>
						</section>
						<section className="evidence-signoff">
							<section className="evidence-signoff-title">
								<EllipsesControl
									content={currentResource.handOffModal.evidenceSignOffTitle}
									tooltip={currentResource.handOffModal.evidenceSignOffTitle}
									noOfLines={1}
									id={currentResource.handOffModal.evidenceSignOffTitle}
								/>
							</section>
							<section className="evidence-signoff-button" key={allSignOff}>
								<Button
									name={labels.preparer}
									variant={allSignOff === 1 ? 'primary' : 'secondary'}
									onClick={() => onAllSignOffChange(1)}
									disabled={evidences.length === 0}
									showLabel
									isEllipsed
									buttonLabel={labels.preparer}
									noOfLines={1}
								/>
								<Button
									name={labels.reviewer}
									variant={allSignOff === 2 ? 'primary' : 'secondary'}
									onClick={() => onAllSignOffChange(2)}
									disabled={evidences.length === 0}
									showLabel
									isEllipsed
									buttonLabel={labels.reviewer}
									noOfLines={1}
								/>
								<Button
									name={labels.none}
									variant={allSignOff === 3 ? 'primary' : 'secondary'}
									onClick={() => onAllSignOffChange(3)}
									disabled={evidences.length === 0}
									showLabel
									isEllipsed
									buttonLabel={labels.none}
									noOfLines={1}
								/>
							</section>
						</section>
					</section>
					<section className="evidence-list customScrollbar">
						<Evidence
							evidences={evidences}
							engagementId={engagementId}
							documentsSignOff={documentsSignOff}
							onDocumentSignOffChange={onDocumentSignOffChange}
						/>
					</section>
				</section>
			)}
		</StyledHandOff>
	);
}

export default forwardRef(HandOff);

const StyledHandOff = styled.section`
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;

	.description {
		display: flex;
		font-size: var(--px-14);
		font-weight: 600;
		margin-bottom: var(--px-20);
	}

	.hand-off-dropdown {
		display: flex;
		width: 25%;
		margin-bottom: var(--px-30);
	}

	.evidence {
		display: flex;
		flex-direction: column;
		width: 100%;
		border-top: var(--px-1) solid var(--neutrals-200);
		padding: var(--px-20) 0;

		.evidence-title {
			display: flex;
			justify-content: flex-end;
			width: 100%;
			margin-bottom: var(--px-10);

			.evidenceTitleWrapper {
				width: calc(100% - var(--px-600));
				display: flex;
			}

			.title {
				display: flex;
				font-size: var(--px-14);
				font-weight: bold;
				width: calc(100% - var(--px-210));
				justify-content: flex-start;
				align-items: center;
				color: var(--neutrals-900);
			}

			.existing-signoffs {
				display: flex;
				font-size: var(--px-14);
				width: var(--px-210);
				font-weight: bold;
				justify-content: flex-start;
				align-items: center;
				color: var(--neutrals-900);

				.ellipses {
					margin-right: var(--px-20);
				}
			}

			.evidence-signoff {
				display: flex;
				flex-direction: row;
				width: var(--px-600);
				justify-content: flex-end;

				.evidence-signoff-title {
					display: flex;
					font-size: var(--px-14);
					font-weight: bold;
					align-items: center;
					color: var(--neutrals-900);
					overflow: auto;
					width: 100%;

					.ellipses {
						justify-content: flex-end;
					}
				}

				.evidence-signoff-button {
					display: flex;
					width: 100%;
					justify-content: flex-end;
					align-items: center;
					width: 65%;
					margin-right: var(--px-20);
					margin-left: var(--px-20);

					.motif-button {
						width: var(--px-130);
						height: var(--px-32);
						.ellipses {
							justify-content: center;
						}
					}
				}
			}
		}

		.evidence-list {
			display: flex;
			width: 100%;
			height: 100%;
			background: var(--neutrals-25);
			max-height: var(--px-400);
			border: var(--px-1) solid var(--neutrals-200);
			border-radius: var(--px-2);
			overflow: overlay;
		}
	}
`;
